<app-page [pageTitle]="pageTitle">
    <ng-container content>
        <!-- Strategic Plan Pyramid -->
        <div
            class="mx-auto"
            [class.max-w-6xl]="objectivesViewMode !== 'both'"
            [class.max-w-full]="objectivesViewMode === 'both'"
        >
            <!-- TIER 1: Vision (Pyramid Apex - Narrowest) -->
            <div class="mx-auto max-w-md">
                <div
                    class="group relative cursor-pointer overflow-hidden rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm transition-all hover:border-primary-300 hover:shadow-md"
                    (click)="strategicSectionDialog?.set(visionData!)"
                >
                    <div
                        class="absolute inset-x-0 top-0 h-1 rounded-t-xl bg-primary-500"
                    ></div>

                    <div
                        class="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-primary-50"
                    >
                        <i class="fa-light fa-eye text-lg text-primary-600"></i>
                    </div>

                    <h2 class="mb-2 text-base font-semibold text-slate-800">
                        {{ 'translate_vision' | translate }}
                    </h2>

                    <p class="text-sm leading-relaxed text-slate-600">
                        {{ visionData?.content }}
                    </p>
                </div>
            </div>

            <!-- Connector -->
            <div class="flex justify-center" aria-hidden="true">
                <div class="h-6 w-px bg-slate-200"></div>
            </div>

            <!-- TIER 2: Mission (Wider) -->
            <div class="mx-auto max-w-2xl">
                <div
                    class="group relative cursor-pointer overflow-hidden rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm transition-all hover:border-primary-300 hover:shadow-md"
                    (click)="strategicSectionDialog?.set(missionData!)"
                >
                    <div
                        class="absolute inset-x-0 top-0 h-1 rounded-t-xl bg-primary-400"
                    ></div>

                    <div
                        class="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-primary-50"
                    >
                        <i
                            class="fa-light fa-bullseye text-lg text-primary-600"
                        ></i>
                    </div>

                    <h2 class="mb-2 text-base font-semibold text-slate-800">
                        {{ 'translate_mission' | translate }}
                    </h2>

                    <p class="text-sm leading-relaxed text-slate-600">
                        {{ missionData?.content }}
                    </p>
                </div>
            </div>

            <!-- Connector -->
            <div class="flex justify-center" aria-hidden="true">
                <div class="h-6 w-px bg-slate-200"></div>
            </div>

            <!-- TIER 3: Strategic Objectives (Full Width - Pyramid Base) -->
            <div
                class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
            >
                <!-- Section Header with accent line -->
                <div
                    class="relative flex items-center justify-center border-b border-slate-100 px-5 py-4"
                >
                    <div
                        class="absolute bottom-0 top-0 w-1 bg-primary-500 ltr:left-0 rtl:right-0"
                    ></div>
                    <h2 class="text-lg font-semibold text-slate-800">
                        {{
                            (objectivesViewMode === 'perspectives' ||
                            objectivesViewMode === 'both'
                                ? 'translate_strategic_perspectives'
                                : isExternalType
                                  ? 'translate_external_pillars'
                                  : 'translate_strategic_objectives'
                            ) | translate
                        }}
                    </h2>
                </div>

                <div class="flex flex-col gap-4 p-5">
                    <ng-template #mapGoalCard let-goal>
                        <a
                            [routerLink]="[
                                '',
                                'system-list',
                                'strategic-goal',
                                'detail',
                                goal.id,
                            ]"
                            class="flex w-full flex-col items-center justify-center rounded-xl border border-primary-200 bg-primary-50 px-4 py-4 text-center no-underline transition-all hover:border-primary-300 hover:no-underline"
                        >
                            @if (goal.code) {
                                <div
                                    class="mb-1 text-xs font-medium text-slate-400"
                                >
                                    {{ goal.code }}
                                </div>
                            }
                            <span
                                class="w-full whitespace-normal break-words text-center text-sm font-medium leading-relaxed text-primary-700"
                            >
                                {{ goal.name }}
                            </span>
                        </a>
                    </ng-template>

                    @if (objectivesViewMode === 'perspectives') {
                        @for (
                            perspective of perspectives;
                            track trackByPerspectiveId($index, perspective)
                        ) {
                            <div
                                class="rounded-lg border border-slate-100 p-5 transition-all hover:border-slate-200"
                            >
                                <div
                                    class="flex flex-wrap items-center gap-2 md:gap-0 xl:items-stretch"
                                >
                                    <div
                                        class="flex w-36 flex-shrink-0 flex-col items-center justify-center rounded-lg bg-slate-50 p-4 md:me-5"
                                    >
                                        <div
                                            class="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary-100"
                                        >
                                            <i
                                                [class]="
                                                    'text-primary-600 ' +
                                                    (perspective.iconClass ||
                                                        'fa-light fa-compass')
                                                "
                                            ></i>
                                        </div>
                                        <div
                                            class="text-center text-sm text-slate-700"
                                            [class.font-extrabold]="
                                                !isRtlLayout
                                            "
                                            [class.font-bold]="isRtlLayout"
                                        >
                                            {{ perspective.name }}
                                        </div>
                                    </div>
                                    <div
                                        class="flex flex-1 flex-wrap items-center justify-center gap-3"
                                    >
                                        @for (
                                            goal of perspective.goals;
                                            track trackByGoalId($index, goal)
                                        ) {
                                            <div class="w-36 sm:w-40 md:w-44">
                                                <ng-container
                                                    *ngTemplateOutlet="
                                                        mapGoalCard;
                                                        context: {
                                                            $implicit: goal,
                                                        }
                                                    "
                                                ></ng-container>
                                            </div>
                                        }
                                    </div>
                                </div>
                            </div>
                        }
                    }

                    @if (objectivesViewMode === 'both') {
                        @for (
                            perspective of perspectives;
                            track trackByPerspectiveId($index, perspective)
                        ) {
                            <div
                                class="rounded-lg border border-slate-100 p-5 transition-all hover:border-slate-200"
                            >
                                <div class="flex flex-row items-stretch gap-4">
                                    <div
                                        class="flex w-28 shrink-0 flex-col items-center justify-center self-stretch rounded-lg bg-slate-50 p-4 sm:w-32"
                                    >
                                        <div
                                            class="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary-100"
                                        >
                                            <i
                                                [class]="
                                                    'text-primary-600 ' +
                                                    (perspective.iconClass ||
                                                        'fa-light fa-compass')
                                                "
                                            ></i>
                                        </div>
                                        <div
                                            class="text-center text-sm font-bold leading-snug text-slate-700"
                                        >
                                            {{ perspective.name }}
                                        </div>
                                    </div>

                                    <div
                                        class="flex min-w-0 flex-1 flex-wrap items-start justify-center gap-4"
                                    >
                                        @for (
                                            pillar of perspective.pillars;
                                            track trackByPillarId(
                                                $index,
                                                pillar
                                            )
                                        ) {
                                            <!-- prettier-ignore -->
                                            <div [class]="perspective.solo ? 'flex w-full flex-col items-stretch gap-3' : 'flex min-w-[14rem] max-w-md flex-1 flex-col items-stretch gap-3'">
                                                @if (
                                                    isSingleNamedPillarGoal(
                                                        pillar
                                                    )
                                                ) {
                                                    <!-- prettier-ignore -->
                                                    <a
                                                        [routerLink]="['', 'system-list', 'strategic-goal', 'detail', pillar.goals[0].id]"
                                                        [class]="perspective.solo ? 'mx-auto flex w-fit max-w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-3 py-3 text-center text-white no-underline transition-colors hover:bg-primary-700 hover:no-underline' : 'flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-3 py-3 text-center text-white no-underline transition-colors hover:bg-primary-700 hover:no-underline'"
                                                    >
                                                        <i
                                                            [class]="
                                                                'shrink-0 text-primary-100 ' +
                                                                (pillar.iconClass ||
                                                                    'fa-light fa-bullseye')
                                                            "
                                                        ></i>
                                                        <span
                                                            class="min-w-0 break-words text-center text-sm font-semibold leading-snug"
                                                        >
                                                            {{ pillar.name }}
                                                        </span>
                                                    </a>
                                                } @else {
                                                    <!-- prettier-ignore -->
                                                    <div [class]="perspective.solo ? 'mx-auto flex w-fit max-w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-3 py-3 text-center text-white' : 'flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-3 py-3 text-center text-white'">
                                                        <i
                                                            [class]="
                                                                'shrink-0 text-primary-100 ' +
                                                                (pillar.iconClass ||
                                                                    'fa-light fa-bullseye')
                                                            "
                                                        ></i>
                                                        <span
                                                            class="min-w-0 break-words text-center text-sm font-semibold leading-snug"
                                                        >
                                                            {{ pillar.name }}
                                                        </span>
                                                    </div>

                                                    <!-- prettier-ignore -->
                                                    <div [class]="perspective.solo ? 'flex flex-wrap items-start justify-center gap-3' : 'contents'">
                                                        @for (
                                                            goal of pillar.goals;
                                                            track trackByGoalId(
                                                                $index,
                                                                goal
                                                            )
                                                        ) {
                                                            <!-- prettier-ignore -->
                                                            <div [class]="perspective.solo ? 'w-36 sm:w-40 md:w-44' : 'contents'">
                                                                <ng-container
                                                                    *ngTemplateOutlet="
                                                                        mapGoalCard;
                                                                        context: {
                                                                            $implicit:
                                                                                goal,
                                                                        }
                                                                    "
                                                                ></ng-container>
                                                            </div>
                                                        }
                                                    </div>
                                                }
                                            </div>
                                        }
                                    </div>
                                </div>
                            </div>
                        }
                    }

                    <!-- Pillar Cards -->
                    @for (
                        pillar of pillarsWithComputedGoals;
                        track trackByPillarId($index, pillar)
                    ) {
                        <div
                            class="rounded-lg border border-slate-100 p-5 transition-all hover:border-slate-200"
                        >
                            <div
                                class="flex flex-wrap items-center gap-2 md:gap-0 xl:items-stretch"
                            >
                                <!-- Pillar Label -->
                                <div
                                    class="flex w-36 flex-shrink-0 flex-col items-center justify-center rounded-lg bg-slate-50 p-4 md:me-5"
                                >
                                    <div
                                        class="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary-100"
                                    >
                                        <i
                                            [class]="
                                                'text-primary-600 ' +
                                                pillar.iconClass
                                            "
                                        ></i>
                                    </div>
                                    <div class="text-center">
                                        <div
                                            class="text-sm text-slate-700"
                                            [class.font-extrabold]="
                                                !isRtlLayout
                                            "
                                            [class.font-bold]="isRtlLayout"
                                        >
                                            {{ pillar.name }}
                                        </div>
                                    </div>
                                </div>
                                <!-- Goals Layout -->
                                <div class="flex flex-1 flex-col gap-3">
                                    <!-- Parent-Child Relationships -->
                                    @for (
                                        goal of pillar.parentChildGoals;
                                        track trackByGoalId($index, goal)
                                    ) {
                                        <div
                                            class="flex flex-row-reverse items-center justify-center gap-3"
                                        >
                                            <!-- Parent Goal Box -->
                                            <div
                                                class="flex min-h-[80px] w-36 flex-shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white p-3 text-center shadow-sm sm:w-40 md:w-44"
                                            >
                                                @if (planType !== 'special') {
                                                    <a
                                                        [routerLink]="[
                                                            '',
                                                            'system-list',
                                                            'strategic-goal',
                                                            'detail',
                                                            goal.id,
                                                        ]"
                                                        class="text-sm font-medium text-slate-700 no-underline hover:text-primary-600 hover:no-underline"
                                                    >
                                                        {{ goal.parentName }}
                                                    </a>
                                                }
                                                @if (planType === 'special') {
                                                    <a
                                                        [routerLink]="[
                                                            '',
                                                            'system-list',
                                                            'strategic-goal',
                                                            'detail',
                                                            $safeNavigationMigration(
                                                                goal
                                                                    .strategicGoal
                                                                    ?.id
                                                            ),
                                                        ]"
                                                        class="text-sm font-medium text-slate-700 no-underline hover:text-primary-600 hover:no-underline"
                                                    >
                                                        {{ goal.parentName }}
                                                    </a>
                                                }
                                            </div>
                                            <!-- Arrow -->
                                            <div class="flex items-center px-1">
                                                <i
                                                    class="fa-light fa-arrow-right text-slate-300"
                                                    [class.rotate-180]="
                                                        isRtlLayout
                                                    "
                                                ></i>
                                            </div>
                                            <!-- Strategic Goal Box -->
                                            @if (planType !== 'special') {
                                                <a
                                                    [routerLink]="[
                                                        '',
                                                        'system-list',
                                                        'strategic-goal',
                                                        'detail',
                                                        goal.id,
                                                    ]"
                                                    class="flex min-h-[80px] w-36 flex-shrink-0 items-center justify-center rounded-xl border border-primary-200 bg-primary-50 p-3 text-center no-underline shadow-sm transition-all hover:border-primary-300 hover:no-underline hover:shadow-md sm:w-40 md:w-44"
                                                >
                                                    <div class="text-center">
                                                        @if (
                                                            planType ===
                                                                'normal' &&
                                                            goal.code
                                                        ) {
                                                            <div
                                                                class="mb-1 inline-block rounded bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-600"
                                                            >
                                                                {{ goal.code }}
                                                            </div>
                                                        }
                                                        <span
                                                            class="text-sm font-medium text-primary-700"
                                                        >
                                                            {{ goal.name }}
                                                        </span>
                                                    </div>
                                                </a>
                                            }
                                            @if (planType === 'special') {
                                                <a
                                                    [routerLink]="[
                                                        '',
                                                        'system-list',
                                                        'strategic-special-goal',
                                                        'detail',
                                                        goal.id,
                                                    ]"
                                                    class="flex min-h-[80px] w-36 flex-shrink-0 items-center justify-center rounded-xl border border-primary-200 bg-primary-50 p-3 text-center no-underline shadow-sm transition-all hover:border-primary-300 hover:no-underline hover:shadow-md sm:w-40 md:w-44"
                                                >
                                                    <span
                                                        class="text-sm font-medium text-primary-700"
                                                    >
                                                        {{ goal.name }}
                                                    </span>
                                                </a>
                                            }
                                        </div>
                                    }
                                    <!-- Standalone Strategic Goals -->
                                    <div
                                        class="flex flex-wrap items-center justify-center gap-3"
                                    >
                                        @for (
                                            goal of pillar.standaloneGoalsFromPillar;
                                            track trackByGoalId($index, goal)
                                        ) {
                                            <a
                                                [routerLink]="
                                                    planType === 'special'
                                                        ? [
                                                              '',
                                                              'system-list',
                                                              'strategic-special-goal',
                                                              'detail',
                                                              goal.id,
                                                          ]
                                                        : [
                                                              '',
                                                              'system-list',
                                                              'strategic-goal',
                                                              'detail',
                                                              goal.id,
                                                          ]
                                                "
                                                class="flex min-h-[80px] w-36 flex-shrink-0 items-center justify-center rounded-xl border border-primary-200 bg-primary-50 p-3 text-center no-underline shadow-sm transition-all hover:border-primary-300 hover:no-underline hover:shadow-md sm:w-40 md:w-44"
                                            >
                                                <div class="text-center">
                                                    @if (
                                                        planType === 'normal' &&
                                                        goal.code
                                                    ) {
                                                        <div
                                                            class="mb-1 inline-block rounded bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-600"
                                                        >
                                                            {{ goal.code }}
                                                        </div>
                                                    }
                                                    <span
                                                        class="text-sm font-medium text-primary-700"
                                                    >
                                                        {{ goal.name }}
                                                    </span>
                                                </div>
                                            </a>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    }

                    <!-- No Pillars Placeholder -->
                    @if (
                        (objectivesViewMode === 'pillars' ||
                            objectivesViewMode === 'special') &&
                        (!pillars || pillars.length === 0)
                    ) {
                        <div
                            class="rounded-lg border border-slate-100 p-5"
                        ></div>
                    }

                    <!-- Standalone Goals Section (Goals without Pillars) -->
                    @if (hasStandaloneGoalsValue) {
                        <div
                            class="rounded-lg border border-slate-100 p-5 transition-all hover:border-slate-200"
                        >
                            <div class="flex items-stretch">
                                <!-- Icon and Label -->
                                @if (planType !== 'special') {
                                    <div
                                        class="me-5 flex w-36 flex-shrink-0 flex-col items-center justify-center rounded-lg bg-slate-50 p-4"
                                    >
                                        <div
                                            class="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary-100"
                                        >
                                            <i
                                                class="fa-light fa-star text-primary-600"
                                            ></i>
                                        </div>
                                        <div class="text-center">
                                            <div
                                                class="text-sm text-slate-700"
                                                [class.font-extrabold]="
                                                    !isRtlLayout
                                                "
                                                [class.font-bold]="isRtlLayout"
                                            >
                                                {{
                                                    standaloneGoalsLabel
                                                        | translate
                                                }}
                                            </div>
                                        </div>
                                    </div>
                                }
                                <!-- Goals Layout -->
                                <div class="flex flex-1 flex-col gap-3">
                                    <!-- Parent-Child Relationships -->
                                    @for (
                                        goal of standaloneParentChildGoals;
                                        track trackByGoalId($index, goal)
                                    ) {
                                        <div
                                            class="flex flex-row-reverse items-center justify-center gap-3"
                                        >
                                            <!-- Parent Goal Box -->
                                            <a
                                                [routerLink]="[
                                                    '',
                                                    'system-list',
                                                    'strategic-goal',
                                                    'detail',
                                                    $safeNavigationMigration(
                                                        goal.strategicGoal?.id
                                                    ),
                                                ]"
                                                class="flex min-h-[80px] w-36 flex-shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white p-3 text-center no-underline shadow-sm transition-all hover:border-primary-300 hover:no-underline hover:shadow-md sm:w-40 md:w-44"
                                            >
                                                <p
                                                    class="text-sm font-medium text-slate-700"
                                                >
                                                    {{ goal.parentName }}
                                                </p>
                                            </a>
                                            <!-- Arrow -->
                                            <div class="flex items-center px-1">
                                                <i
                                                    class="fa-light fa-arrow-right text-slate-300"
                                                    [class.rotate-180]="
                                                        isRtlLayout
                                                    "
                                                ></i>
                                            </div>
                                            <!-- Strategic Goal Box -->
                                            @if (planType !== 'special') {
                                                <a
                                                    [routerLink]="[
                                                        '',
                                                        'system-list',
                                                        'strategic-goal',
                                                        'detail',
                                                        goal.id,
                                                    ]"
                                                    class="flex min-h-[80px] w-36 flex-shrink-0 items-center justify-center rounded-xl border border-primary-200 bg-primary-50 p-3 text-center no-underline shadow-sm transition-all hover:border-primary-300 hover:no-underline hover:shadow-md sm:w-40 md:w-44"
                                                >
                                                    <div class="text-center">
                                                        @if (
                                                            planType ===
                                                                'normal' &&
                                                            goal.code
                                                        ) {
                                                            <div
                                                                class="mb-1 inline-block rounded bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-600"
                                                            >
                                                                {{ goal.code }}
                                                            </div>
                                                        }
                                                        <span
                                                            class="text-sm font-medium text-primary-700"
                                                        >
                                                            {{ goal.name }}
                                                        </span>
                                                    </div>
                                                </a>
                                            }
                                            @if (planType === 'special') {
                                                <a
                                                    [routerLink]="[
                                                        '',
                                                        'system-list',
                                                        'strategic-special-goal',
                                                        'detail',
                                                        goal.id,
                                                    ]"
                                                    class="flex min-h-[80px] w-36 flex-shrink-0 items-center justify-center rounded-xl border border-primary-200 bg-primary-50 p-3 text-center no-underline shadow-sm transition-all hover:border-primary-300 hover:no-underline hover:shadow-md sm:w-40 md:w-44"
                                                >
                                                    <span
                                                        class="text-sm font-medium text-primary-700"
                                                    >
                                                        {{ goal.name }}
                                                    </span>
                                                </a>
                                            }
                                        </div>
                                    }
                                    <!-- Standalone Strategic Goals -->
                                    <div
                                        class="flex flex-wrap items-center justify-center gap-3"
                                    >
                                        @for (
                                            goal of standaloneGoalsFromPillar;
                                            track trackByGoalId($index, goal)
                                        ) {
                                            <a
                                                [routerLink]="
                                                    planType === 'special'
                                                        ? [
                                                              '',
                                                              'system-list',
                                                              'strategic-special-goal',
                                                              'detail',
                                                              goal.id,
                                                          ]
                                                        : [
                                                              '',
                                                              'system-list',
                                                              'strategic-goal',
                                                              'detail',
                                                              goal.id,
                                                          ]
                                                "
                                                class="flex min-h-[80px] w-36 flex-shrink-0 items-center justify-center rounded-xl border border-primary-200 bg-primary-50 p-3 text-center no-underline shadow-sm transition-all hover:border-primary-300 hover:no-underline hover:shadow-md sm:w-40 md:w-44"
                                            >
                                                <div class="text-center">
                                                    @if (
                                                        planType === 'normal' &&
                                                        goal.code
                                                    ) {
                                                        <div
                                                            class="mb-1 inline-block rounded bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-600"
                                                        >
                                                            {{ goal.code }}
                                                        </div>
                                                    }
                                                    <span
                                                        class="text-sm font-medium text-primary-700"
                                                    >
                                                        {{ goal.name }}
                                                    </span>
                                                </div>
                                            </a>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>

        <!-- Values Section -->
        @if (values && values.length > 0) {
            <div class="mx-auto mt-6 max-w-6xl overflow-hidden">
                <!-- Header -->
                <div class="mb-4 text-center">
                    <h2 class="text-lg font-semibold text-slate-800">
                        {{ 'translate_values' | translate }}
                    </h2>
                    <div
                        class="mx-auto mt-2 h-0.5 w-16 rounded-full bg-primary-200"
                    ></div>
                </div>
                <!-- Values Grid -->
                <div class="flex flex-wrap gap-3">
                    @for (value of values; track trackByValue($index, value)) {
                        <div
                            class="flex-1 rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm transition-all hover:border-primary-200 hover:shadow-md"
                        >
                            <p
                                class="whitespace-normal break-words text-sm font-semibold text-slate-700"
                            >
                                {{ value.name }}
                            </p>
                            @if (value.description) {
                                <p
                                    class="mt-1 whitespace-normal break-words text-xs text-slate-500"
                                >
                                    {{ value.description }}
                                </p>
                            }
                        </div>
                    }
                </div>
            </div>
        }
    </ng-container>
</app-page>
